import { useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';

/**
 * "Comment" — file a note against the batch's job(s) (PLM Phase 4).
 *
 * Port of the legacy Comment popup (`Comment_Execute` :2714). A QC batch's
 * `Jobid` is a ';'-delimited list and legacy writes ONE `dbo.Notes` row per
 * job, so the comment reaches every job the batch touches — the dialog says so
 * when there is more than one.
 *
 * Both fields are required; legacy refuses with "Fill the comment details."
 * and so does the server.
 */

export interface PlmCommentDialogProps {
  open: boolean;
  /** The batch label, shown so the analyst knows what they are commenting on. */
  batchId: string | null;
  /** How many jobs the batch spans, if known — drives the "one note per job" hint. */
  jobCount?: number;
  onCancel: () => void;
  onSave: (title: string, text: string) => void | Promise<void>;
}

export function PlmCommentDialog({
  open, batchId, jobCount, onCancel, onSave,
}: PlmCommentDialogProps) {
  const [title, setTitle] = useState('');
  const [text, setText]   = useState('');

  const complete = title.trim().length > 0 && text.trim().length > 0;

  return (
    <AppDialog
      open={open}
      title={batchId ? `Comment — ${batchId}` : 'Comment'}
      icon="ti-notes"
      onClose={onCancel}
      width={520}
      // Enter belongs to the multi-line body, not the footer.
      submitOnEnter={false}
      footer={
        <>
          <button
            type="button"
            className="ca-btn ca-btn-solid"
            disabled={!complete}
            onClick={() => { void onSave(title.trim(), text.trim()); }}
          >
            Save
          </button>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
        </>
      }
    >
      <div className="plm-comment">
        <div className="plm-field">
          <label htmlFor="plm-comment-title">Title <span className="plm-req">*</span></label>
          <input
            id="plm-comment-title"
            type="text"
            className={`plm-input${title.trim() ? '' : ' plm-input--required'}`}
            value={title}
            maxLength={400}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        <div className="plm-field">
          <label htmlFor="plm-comment-text">Comment <span className="plm-req">*</span></label>
          <textarea
            id="plm-comment-text"
            className={`plm-input plm-comment__body${text.trim() ? '' : ' plm-input--required'}`}
            rows={6}
            value={text}
            onChange={e => setText(e.target.value)}
          />
        </div>

        {jobCount !== undefined && jobCount > 1 && (
          <p className="plm-comment__hint">
            This batch covers {jobCount} jobs — the comment is filed against each
            of them.
          </p>
        )}
      </div>
    </AppDialog>
  );
}
